import type { CostTier, ModelRole, ModelRow, PendingBooFeature, PurposeDef, PurposeDefView } from "./api.ts";

// Display helpers for model slugs ("vendor/model-id"). Slugs stay the source
// of truth everywhere; these only make them readable.

const VENDOR_NAMES: Record<string, string> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  google: "Google",
  deepseek: "DeepSeek",
  moonshotai: "Moonshot",
  minimax: "MiniMax",
  voyage: "Voyage",
  typesafe: "Typesafe",
  "meta-llama": "Meta",
  mistralai: "Mistral",
  "x-ai": "xAI",
  "z-ai": "Z.ai",
  qwen: "Qwen",
  cohere: "Cohere",
};

const WORD_CASE: Record<string, string> = {
  gpt: "GPT",
  deepseek: "DeepSeek",
  minimax: "MiniMax",
  glm: "GLM",
  llama: "Llama",
  jev: "Jev",
};

export function vendorOf(slug: string): string {
  const i = slug.indexOf("/");
  return i === -1 ? "" : slug.slice(0, i);
}

export function modelIdOf(slug: string): string {
  const i = slug.indexOf("/");
  return i === -1 ? slug : slug.slice(i + 1);
}

export function vendorName(vendor: string): string {
  return VENDOR_NAMES[vendor] ?? vendor.charAt(0).toUpperCase() + vendor.slice(1);
}

// "claude-sonnet-4-6" → "Claude Sonnet 4.6", "deepseek-v4.1-flash" →
// "DeepSeek V4.1 Flash", "claude-sonnet-4-20250514" → "Claude Sonnet 4",
// "o3-mini" → "o3 Mini". Custom endpoint ids are shown as typed.
export function modelName(slug: string, opts: { custom?: boolean } = {}): string {
  const id = modelIdOf(slug);
  if (opts.custom) return id;
  // Release dates are not part of the name: -20250514 or -2024-08-06.
  const tokens = id.replace(/-\d{8}$/, "").replace(/-\d{4}-\d{2}-\d{2}$/, "").split("-");
  const words: string[] = [];
  for (const token of tokens) {
    const prev = words[words.length - 1];
    // Version parts are short: "4-6" → "4.6". Longer runs stay separate.
    if (/^\d{1,2}$/.test(token) && prev && /(^|[^\d])\d{1,2}$/.test(prev)) {
      words[words.length - 1] = `${prev}.${token}`;
      continue;
    }
    const lower = token.toLowerCase();
    if (WORD_CASE[lower]) words.push(WORD_CASE[lower]);
    else if (/^o\d+$/.test(token)) words.push(token); // OpenAI's o-series is lowercase
    else if (/^[a-z]\d/.test(token) || /^\d+(\.\d+)?[bm]$/i.test(token)) words.push(token.toUpperCase());
    else words.push(token.charAt(0).toUpperCase() + token.slice(1));
  }
  return words.join(" ");
}

export const COST_TIERS: Record<CostTier, { label: string; marks: string }> = {
  economy: { label: "Economy", marks: "$" },
  standard: { label: "Standard", marks: "$$" },
  premium: { label: "Premium", marks: "$$$" },
  frontier: { label: "Frontier", marks: "$$$$" },
};

export function isChatClass(def: Pick<PurposeDef, "id" | "fallback">): boolean {
  return def.id === "chat" || def.fallback === "chat";
}

// The pick that turns a specialty task (transcription, search, quick
// decisions) off. No pick = the Boo's default.
export const MODEL_OFF = "off";

// Group by vendor in catalog order, vendors in first-seen order.
export function groupByVendor<T extends { name: string }>(models: T[]): Array<{ vendor: string; models: T[] }> {
  const groups = new Map<string, T[]>();
  for (const m of models) {
    const v = vendorOf(m.name);
    if (!groups.has(v)) groups.set(v, []);
    groups.get(v)!.push(m);
  }
  return [...groups.entries()].map(([vendor, list]) => ({ vendor, models: list }));
}

// What a model is for, in the words the jobs use.
export const ROLE_LABELS: Record<ModelRole, string> = {
  chat: "Conversations and other jobs",
  transcription: "Voice to text",
  embeddings: "Searching memory",
  decision: "Quick yes/no calls",
};

// Each job named by what Boo does. Boos send their own labels; these read
// better to someone who has never configured a model. Unknown jobs keep the
// Boo's wording.
const JOBS: Record<string, { label: string; description: string }> = {
  chat: { label: "Conversations", description: "Everything Boo says and does in your chats." },
  intents: { label: "Sorting incoming messages", description: "Decides which messages need Boo and writes the approval requests it sends you." },
  subagents: { label: "Helpers on big jobs", description: "Extra helpers Boo starts to split up large pieces of work." },
  heartbeats: { label: "Scheduled check-ins", description: "Check-ins and follow-ups Boo runs on a schedule." },
  workflow: { label: "Automated workflows", description: "Multi-step work Boo runs on its own." },
  retrospect: { label: "Learning from past work", description: "Looks back at finished work so Boo does better next time." },
  indexing: { label: "Reading documents", description: "Reads and summarizes documents so Boo can remember them." },
  coding: { label: "Writing code", description: "Code Boo writes and runs in its own workspace." },
  transcription: { label: "Voice to text", description: "Turns voice notes and calls into text." },
  embeddings: { label: "Searching memory", description: "Finds the right memories and documents for a question." },
  decisions: { label: "Quick yes/no calls", description: "Small routing and yes/no decisions." },
};

export function jobLabel(def: Pick<PurposeDef, "id" | "label">): string {
  return JOBS[def.id]?.label ?? def.label;
}

export function jobDescription(def: Pick<PurposeDef, "id" | "description">): string {
  return JOBS[def.id]?.description ?? def.description;
}

// The same purpose definitions with the job names above, for anything that
// shows them (pickers, test results).
export function withJobNames<T extends PurposeDef>(defs: T[]): T[] {
  return defs.map((d) => ({ ...d, label: jobLabel(d), description: jobDescription(d) }));
}

export const TURNED_OFF_NOTE = "Turned off for your organization";

// In a picker, for people who can't add endpoints.
export const ASK_ADMIN = "Ask an organization admin to add your own model";

// "Ops Boo", "Ops Boo and Sales Boo", "Ops Boo, Sales Boo and 3 more".
export function joinNames(names: string[], max = 3): string {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length <= max) return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `${names.slice(0, max - 1).join(", ")} and ${names.length - (max - 1)} more`;
}

// Names of the Boos that don't apply a feature yet.
export function pendingFor(
  pending: Array<{ name: string; missing: PendingBooFeature[] }>,
  feature: PendingBooFeature,
): string[] {
  return pending.filter((b) => b.missing.includes(feature)).map((b) => b.name);
}

// Manage models groups: custom models by endpoint (endpoints in the given
// order), then hosted models by vendor in catalog order.
export function groupModelRows(
  rows: ModelRow[],
  endpointOrder: string[],
): Array<{ key: string; kind: "custom" | "hosted"; name: string; models: ModelRow[] }> {
  const custom = new Map<string, ModelRow[]>(endpointOrder.map((n) => [n, []]));
  const hosted: ModelRow[] = [];
  for (const r of rows) {
    if (r.source === "custom" && r.endpoint) {
      if (!custom.has(r.endpoint)) custom.set(r.endpoint, []);
      custom.get(r.endpoint)!.push(r);
    } else hosted.push(r);
  }
  return [
    ...[...custom.entries()].map(([name, models]) => ({ key: `custom:${name}`, kind: "custom" as const, name, models })),
    ...groupByVendor(hosted).map((g) => ({ key: `hosted:${g.vendor}`, kind: "hosted" as const, name: g.vendor, models: g.models })),
  ];
}

// Whether a job can run on your own models. Coding can't on backends that
// don't say.
export function acceptsCustom(def: Pick<PurposeDefView, "id" | "acceptsCustom">): boolean {
  return def.acceptsCustom ?? def.id !== "coding";
}

export const BUILT_IN_ONLY = "Writing code uses built-in models only.";

// A model source's display name: its label, else its id made readable.
export function sourceLabel(src: { name: string; label?: string | null }): string {
  // The backend sends the id itself when there is no label.
  if (src.label?.trim() && src.label.trim() !== src.name) return src.label.trim();
  return src.name
    .split("-")
    // Two-letter parts are usually initials: "northwind-ai" → "Northwind AI".
    .map((w) => (w.length === 2 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
}

// What a job can't do when a Boo turns it off.
export const OFF_CONSEQUENCE: Record<string, string> = {
  transcription: "Boo won't turn voice notes into text",
  embeddings: "Boo won't search its memory",
  decisions: "Boo won't make quick yes/no calls",
};

// The jobs list in a sentence: "Conversations", "Conversations and Sorting
// incoming messages".
export function jobsSentence(ids: string[], defs: Array<Pick<PurposeDef, "id" | "label">>): string {
  return joinNames(
    ids.map((id) => {
      const def = defs.find((d) => d.id === id);
      return def ? jobLabel(def) : id;
    }),
    4,
  );
}
